import { useEffect, useState } from "react";
import { Users } from "lucide-react";
import api from "@/lib/api";
import { getQueueState, subscribeQueueState } from "@/lib/queueGate";

// Intervalle de polling du statut — assez court pour rester réactif, assez
// long pour ne pas bombarder le serveur pendant un pic de trafic (c'est
// justement ce que cette page existe pour éviter).
const POLL_MS = 3000;

// Page générique affichée à un visiteur quand la capacité de la boutique
// (shops.storefront_capacity) est atteinte, à la place d'un appel API qui
// échouerait silencieusement. Montée par App.js dès que le store global
// queueGate signale un statut "queued" (voir lib/api.js, intercepteur de
// réponse sur les 503 { queued: true }).
export default function WaitingRoom() {
  const [state, setState] = useState(getQueueState());

  useEffect(() => subscribeQueueState(setState), []);

  useEffect(() => {
    if (!state) return undefined;
    let cancelled = false;
    const poll = async () => {
      try {
        const { data } = await api.get("/queue/status");
        if (!cancelled && data?.status === "active") {
          // Un rechargement complet plutôt qu'un simple changement d'état :
          // toutes les données que les appels bloqués pendant l'attente
          // n'ont pas pu charger doivent repartir d'un chargement propre.
          window.location.reload();
        }
      } catch {
        // Encore en file : l'intercepteur global de lib/api.js a déjà mis à
        // jour la position via setQueueState — rien de plus à faire ici.
      }
    };
    const id = setInterval(poll, POLL_MS);
    return () => { cancelled = true; clearInterval(id); };
  }, [state]);

  if (!state) return null;

  return (
    <div className="min-h-screen flex items-center justify-center bg-cream px-6 py-12">
      <div className="max-w-md w-full text-center">
        <div className="mx-auto mb-6 h-12 w-12 rounded-full border-2 border-ink border-t-transparent animate-spin" />

        <p className="text-xs uppercase tracking-[0.2em] text-ash mb-2">
          {state.shopName || "Cette boutique"}
        </p>
        <h1 className="font-serif text-3xl text-ink mb-3">Forte affluence en ce moment</h1>
        <p className="text-graphite mb-8">
          Nous accueillons les visiteurs un par un pour garantir une expérience fluide à tout le
          monde. Cette page se rafraîchira automatiquement dès que ce sera votre tour — inutile de
          recharger vous-même.
        </p>

        <div className="bg-white border border-stone rounded-sm p-8">
          <p className="text-6xl font-mono font-semibold text-ink tabular-nums leading-none">
            {state.position}
          </p>
          <p className="text-xs uppercase tracking-[0.15em] text-ash mt-3">Votre position dans la file</p>

          <div className="h-px bg-stone my-6" />

          <p className="flex items-center justify-center gap-2 text-sm text-graphite">
            <Users className="h-4 w-4 text-copper" />
            <span>
              <strong className="text-ink">{state.waiting}</strong> personne{state.waiting > 1 ? "s" : ""} en attente pour cette boutique
            </span>
          </p>
        </div>
      </div>
    </div>
  );
}
